import { useQuery } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { createContext, useContext, type ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { api, ApiError } from "../api/client";
import type { Me, Permission } from "../api/types";
import { EmptyState, ErrorState } from "../ui/Notice";
import { FullPageSpinner } from "../ui/Spinner";

export const ME_QUERY_KEY = ["me"] as const;

export function useMeQuery() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: ({ signal }) => api<Me>("/api/auth/me", { signal }),
    retry: (count, error) => !(error instanceof ApiError && !error.isRetryable) && count < 2,
    staleTime: 60_000,
  });
}

const MeContext = createContext<Me | null>(null);

/** The signed-in person. Only usable inside <RequireAuth>. */
export function useMe(): Me {
  const me = useContext(MeContext);
  if (!me) throw new Error("useMe() used outside <RequireAuth>");
  return me;
}

export function can(me: Me, permission: Permission): boolean {
  return me.permissions.includes(permission);
}

export function signInUrl(returnTo: string): string {
  return `/api/auth/signin?return_to=${encodeURIComponent(returnTo)}`;
}

export async function signOut(): Promise<void> {
  try {
    const { logout_url } = await api<{ logout_url: string }>("/api/auth/signout", { method: "POST" });
    window.location.assign(logout_url);
  } catch {
    // Signing out locally failed (network); still leave the app rather than pretend.
    window.location.assign("/signin?signed_out=1");
  }
}

/** Gate for every signed-in page. Unauthenticated visitors go to /signin and come back after. */
export function RequireAuth() {
  const me = useMeQuery();
  const location = useLocation();

  if (me.isPending) {
    return <FullPageSpinner />;
  }
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      const returnTo = `${location.pathname}${location.search}`;
      return <Navigate to={`/signin?return_to=${encodeURIComponent(returnTo)}`} replace />;
    }
    return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;
  }
  return (
    <MeContext.Provider value={me.data}>
      <Outlet />
    </MeContext.Provider>
  );
}

export function RequirePermission({ permission, children }: { permission: Permission; children: ReactNode }) {
  const me = useMe();
  if (!can(me, permission)) {
    return (
      <div className="card" role="alert">
        <EmptyState icon={<Lock size={20} />} title="You don't have access to this area.">
          <p>If you need it, ask a support desk admin.</p>
        </EmptyState>
      </div>
    );
  }
  return <>{children}</>;
}
